import React, { useEffect, useState } from "react";
import { MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons";
import { faDiscord } from "@fortawesome/free-brands-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../images/IconOnly_Transparent_NoBuffer.png";
import { Link } from "react-router-dom";
const navigation = [
  { name: "Pricing", href: "/pricing" },
  { name: "Fee Calculator", href: "/calculator" },
  { name: "About", href: "/about" },
  { name: "Contact Us", href: "/contact-us" },
];
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, false);
    // Read the dark mode preference from localStorage on mount
    const storedDarkMode = localStorage.getItem("darkMode");
    if (storedDarkMode === "true") {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    } else {
      setDarkMode(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    if (newDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", newDarkMode.toString());
  };

  const handleScroll = () => {
    if (
      window.location.pathname === "/signup" ||
      window.location.pathname === "/login"
    ) {
      return;
    }
    const header = document.getElementById("header");
    if (window.scrollY > 50) {
      header!.classList.add("bg-white", "shadow-md");
      header!.classList.remove("bg-transparent");
    } else {
      header!.classList.add("bg-white");
      header!.classList.remove("bg-transparent", "shadow-md");
    }
  };

  return (
    <header
      id={"header"}
      className="bg-white transition-colors duration-100 fixed inset-x-0 top-0 z-50 stroke-gray-200 dark:bg-gray-800"
    >
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between gap-x-6 p-6 lg:px-8"
      >
        <div className="flex lg:flex-1 items-center">
          <Link to="/" className="-m-1.5 p-1.5 flex items-center">
            <img src={logo} alt="TrackNList Logo" className="h-8 w-auto" />
            <span className="ml-2 text-xl tracking-wide font-avenir font-avenir-black bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple bg-clip-text text-transparent hidden lg:block">
              TrackNList
            </span>
          </Link>
        </div>
        <div className="hidden lg:flex lg:gap-x-12">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="text-sm font-semibold leading-6 text-gray-900 dark:text-white"
            >
              {item.name}
            </Link>
          ))}
          {/* /blog is served by the Ghost service via the ingress, not a React
              route — use a hard anchor so the browser leaves the SPA. */}
          <a
            href="/blog"
            className="text-sm font-semibold leading-6 text-gray-900 dark:text-white"
          >
            Blog
          </a>
        </div>
        <div className="flex flex-1 items-center justify-end gap-x-6">
          <Link
            to="/login"
            className="hidden lg:block lg:text-sm lg:font-semibold lg:leading-6 lg:text-gray-900 dark:text-white"
          >
            Log in
          </Link>
          <motion.div whileHover={{ scale: 1.1 }}>
            <Link
              to="/signup"
              className="hidden sm:block rounded-md bg-softBlue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brightPurple focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-softBlue"
            >
              Sign up
            </Link>
          </motion.div>
          <Link
            to="https://discord.gg/52gBH7zNF4"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <FontAwesomeIcon icon={faDiscord} className="h-6 w-6" />
          </Link>
          <button
            onClick={toggleDarkMode}
            className="hidden lg:block text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
          >
            {/*<FontAwesomeIcon icon={darkMode ? faSun : faMoon} className="h-6 w-6" />*/}
            {!darkMode ? (
              <MoonIcon className={"h-6 w-6"} />
            ) : (
              <SunIcon className={"h-6 w-6"} />
            )}
          </button>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-300"
          >
            <span className="sr-only">Open main menu</span>
            <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[100] bg-black/20 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              key="mobile-menu"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 w-80 max-w-[85vw] overflow-y-auto bg-white dark:bg-gray-800 z-[100] shadow-2xl"
            >
              <div className="h-full flex flex-col">
                {/* Header Section */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <Link
                    to="/"
                    className="flex items-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <img
                      src={logo}
                      alt="TrackNList Logo"
                      className="h-8 w-auto"
                    />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-lg p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className="sr-only">Close menu</span>
                    <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                  </button>
                </div>

                {/* Navigation Section */}
                <div className="flex-1 px-4 py-6 space-y-1">
                  {navigation.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center rounded-xl px-4 py-3 text-base font-semibold text-gray-900 dark:text-white hover:bg-gradient-to-r hover:from-softBlue/10 hover:to-brightPurple/10 dark:hover:from-softBlue/20 dark:hover:to-brightPurple/20 transition-all duration-200"
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  ))}
                  {/* Hard anchor: /blog lives on the Ghost service, not the SPA. */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navigation.length * 0.1 }}
                  >
                    <a
                      href="/blog"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center rounded-xl px-4 py-3 text-base font-semibold text-gray-900 dark:text-white hover:bg-gradient-to-r hover:from-softBlue/10 hover:to-brightPurple/10 dark:hover:from-softBlue/20 dark:hover:to-brightPurple/20 transition-all duration-200"
                    >
                      Blog
                    </a>
                  </motion.div>
                </div>

                {/* Action Buttons Section */}
                <div className="px-4 pb-6 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center rounded-xl px-4 py-3 text-base font-semibold text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Log in
                    </Link>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      to="/signup"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center rounded-xl bg-gradient-to-r from-softBlue to-brightPurple px-4 py-3 text-base font-semibold text-white shadow-lg hover:shadow-xl hover:from-softBlue/90 hover:to-brightPurple/90 transition-all duration-200"
                    >
                      Sign up
                    </Link>
                  </motion.div>

                  <div className="pt-2 space-y-2">
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <Link
                        to="https://discord.gg/52gBH7zNF4"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center rounded-xl px-4 py-3 text-base font-semibold text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <FontAwesomeIcon
                          icon={faDiscord}
                          className="mr-3 h-5 w-5"
                        />
                        Join Discord
                      </Link>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 }}
                    >
                      <button
                        onClick={toggleDarkMode}
                        className="flex items-center w-full rounded-xl px-4 py-3 text-base font-semibold text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        {!darkMode ? (
                          <MoonIcon className="mr-3 h-5 w-5" />
                        ) : (
                          <SunIcon className="mr-3 h-5 w-5" />
                        )}
                        {darkMode ? "Light Mode" : "Dark Mode"}
                      </button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
